import React, { useEffect, useMemo, useState } from "react";
import {
  Box,
  Typography,
  Chip,
  Button,
  IconButton,
  ToggleButtonGroup,
  ToggleButton,
  Skeleton,
  Dialog,
  DialogTitle,
  DialogContent,
  Divider,
  Avatar,
  useTheme,
  alpha,
} from "@mui/material";
import CalendarTodayIcon from "@mui/icons-material/CalendarTodayOutlined";
import ViewWeekOutlinedIcon from "@mui/icons-material/ViewWeekOutlined";
import CalendarMonthOutlinedIcon from "@mui/icons-material/CalendarMonthOutlined";
import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import CloseIcon from "@mui/icons-material/Close";
import GroupsOutlinedIcon from "@mui/icons-material/GroupsOutlined";
import LocationOnOutlinedIcon from "@mui/icons-material/LocationOnOutlined";
import LaptopMacOutlinedIcon from "@mui/icons-material/LaptopMacOutlined";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import HowToRegOutlinedIcon from "@mui/icons-material/HowToRegOutlined";
import LaunchIcon from "@mui/icons-material/Launch";
import dayjs, { Dayjs } from "dayjs";
import { useGetStaffTeachingProfileQuery, useGetStaffCalendarQuery } from "../../../../Api/attoDeskApi";
import {
  buildMonthGrid,
  groupEventsByDate,
  buildWeekPlanner,
  activeWeekDays,
  eventColor,
  eventTitle,
  timeRange,
  fmtTime,
} from "../staffCalendar";

const WEEKDAY_HEADERS = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];
const SESSION_LIMIT = 4;

interface Props {
  staffId: string;
  mode: "week" | "calendar";
  onModeChange: (m: "week" | "calendar") => void;
  highlight?: boolean;
  // View Class + Edit Schedule are CLASS-level actions and receive the
  // recurring class id (assign_classes.id). Mark Attendance is an
  // OCCURRENCE-level action and receives the specific dated session id.
  onViewClass?: (classId: number) => void;
  onEditSchedule?: (classId: number) => void;
  onMarkAttendance?: (occurrenceId?: number) => void;
  onViewFullSchedule?: () => void;
  canEditSchedule?: boolean;
  canMarkAttendance?: boolean;
}

const todayKey = dayjs().format("dddd").toLowerCase();

const WeeklySchedulePlanner: React.FC<Props> = ({
  staffId, mode, onModeChange, highlight,
  onViewClass, onEditSchedule, onMarkAttendance, onViewFullSchedule,
  canEditSchedule, canMarkAttendance,
}) => {
  const theme = useTheme();

  // Week view → recurring weekly pattern (teaching-profile).
  const { data: tpData, isFetching: tpFetching } = useGetStaffTeachingProfileQuery({ staffId });
  const weekly = (tpData as any)?.data?.weekly_schedule ?? {};
  const weekPlanner = useMemo(() => buildWeekPlanner(weekly), [weekly]);

  // Calendar view → dated occurrences for the visible month grid. Always
  // opens on the *current* month; when the parent swaps to another staff
  // profile we snap back to today so David Ratno's month never appears under
  // Galaxy Deena's profile. (The parent also passes `key={staffId}` for a
  // hard remount, but the effect below is defence-in-depth in case that
  // guarantee ever loosens.)
  const [month, setMonth] = useState<Dayjs>(dayjs().startOf("month"));
  const monthGrid = useMemo(() => buildMonthGrid(month), [month]);
  const gridStart = monthGrid[0][0].iso;
  const gridEnd = monthGrid[monthGrid.length - 1][6].iso;
  const { data: calData, isFetching: calFetching } = useGetStaffCalendarQuery(
    { staffId, start: gridStart, end: gridEnd },
    { skip: mode !== "calendar" }
  );
  const events = useMemo(() => (Array.isArray((calData as any)?.data) ? ((calData as any).data as any[]) : []), [calData]);
  const eventsByDate = useMemo(() => groupEventsByDate(events), [events]);

  const [expandedDay, setExpandedDay] = useState<string | null>(null);
  const [selectedEvent, setSelectedEvent] = useState<any | null>(null);

  // Reset transient view state on staff swap so the previous teacher's month,
  // expanded day or open event modal never bleed onto the new profile.
  useEffect(() => {
    setMonth(dayjs().startOf("month"));
    setExpandedDay(null);
    setSelectedEvent(null);
  }, [staffId]);

  const ring = highlight ? { boxShadow: `0 0 0 2px ${alpha(theme.palette.primary.main, 0.35)}` } : {};

  return (
    <Box id="staff-weekly-schedule" sx={{
      bgcolor: "#fff", borderRadius: "14px", border: "1px solid rgba(0,0,0,0.06)",
      boxShadow: "0 1px 3px rgba(0,0,0,0.03)", overflow: "hidden", scrollMarginTop: 80, ...ring,
    }}>
      {/* Header */}
      <Box sx={{
        display: "flex", alignItems: { xs: "flex-start", sm: "center" }, justifyContent: "space-between",
        gap: 1.5, flexWrap: "wrap", px: 2.5, py: 1.75, borderBottom: "1px solid rgba(0,0,0,0.05)",
      }}>
        <Box sx={{ display: "flex", alignItems: "center", gap: 1.25, minWidth: 0 }}>
          <Box sx={{ width: 32, height: 32, borderRadius: "9px", bgcolor: alpha("#0ea5e9", 0.1), color: "#0ea5e9", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
            <CalendarTodayIcon sx={{ fontSize: 17 }} />
          </Box>
          <Box sx={{ minWidth: 0 }}>
            <Typography sx={{ fontSize: 14, fontWeight: 700, letterSpacing: "-0.01em", lineHeight: 1.2 }}>Weekly Teaching Schedule</Typography>
            <Typography sx={{ fontSize: 11, color: "text.secondary" }}>View recurring teaching patterns and dated class occurrences for this staff member.</Typography>
          </Box>
        </Box>
        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          <ToggleButtonGroup
            size="small" exclusive value={mode}
            onChange={(_, v) => { if (v) onModeChange(v); }}
            aria-label="Schedule view"
            sx={{ "& .MuiToggleButton-root": { textTransform: "none", fontSize: 12, px: 1.5, py: 0.4, gap: 0.5, border: "1px solid rgba(0,0,0,0.1)" } }}
          >
            <ToggleButton value="week" aria-label="Week view"><ViewWeekOutlinedIcon sx={{ fontSize: 15 }} /> Week</ToggleButton>
            <ToggleButton value="calendar" aria-label="Calendar view"><CalendarMonthOutlinedIcon sx={{ fontSize: 15 }} /> Calendar</ToggleButton>
          </ToggleButtonGroup>
          {onViewFullSchedule && (
            <Button size="small" onClick={onViewFullSchedule} sx={{ textTransform: "none", fontSize: 11.5, color: "text.secondary" }}>View Full Schedule</Button>
          )}
        </Box>
      </Box>

      <Box sx={{ p: 2.5 }}>
        {mode === "week" ? (
          <WeekView planner={weekPlanner} loading={tpFetching && !tpData} expandedDay={expandedDay} setExpandedDay={setExpandedDay} onViewClass={onViewClass} />
        ) : (
          <CalendarView
            month={month} setMonth={setMonth} grid={monthGrid} eventsByDate={eventsByDate}
            loading={calFetching && !calData} onEventClick={setSelectedEvent} eventCount={events.length}
          />
        )}
      </Box>

      {/* Event details modal */}
      <EventModal
        event={selectedEvent} onClose={() => setSelectedEvent(null)}
        onViewClass={onViewClass} onEditSchedule={onEditSchedule} onMarkAttendance={onMarkAttendance}
        canEditSchedule={canEditSchedule} canMarkAttendance={canMarkAttendance}
      />
    </Box>
  );
};

// ── Week view (7-day planner) ──────────────────────────────────────────────
const WeekView: React.FC<{
  planner: { key: string; label: string; slots: any[] }[];
  loading: boolean;
  expandedDay: string | null;
  setExpandedDay: (d: string | null) => void;
  onViewClass?: (id: number) => void;
}> = ({ planner, loading, expandedDay, setExpandedDay, onViewClass }) => {
  const total = planner.reduce((s, d) => s + d.slots.length, 0);
  // Only render weekdays the teacher actually has classes on — no empty
  // Saturday/Sunday columns. Column count follows the number of active days so
  // 1 day is one full-width column, 3 days three columns, etc. (never blank
  // stretched columns).
  const activeDays = activeWeekDays(planner);
  const cols = Math.min(Math.max(activeDays.length, 1), 7);

  if (loading) {
    return (
      <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", sm: "repeat(auto-fill, minmax(160px, 1fr))" }, gap: 1.5 }}>
        {Array.from({ length: 7 }).map((_, i) => <Skeleton key={i} variant="rounded" height={140} />)}
      </Box>
    );
  }
  if (total === 0) {
    return (
      <Box sx={{ textAlign: "center", py: 5 }}>
        <CalendarTodayIcon sx={{ fontSize: 34, color: "rgba(0,0,0,0.1)", mb: 1 }} />
        <Typography sx={{ fontSize: 13, color: "text.secondary" }}>No weekly classes assigned</Typography>
      </Box>
    );
  }

  return (
    // Columns = active teaching days only. Stacks to a single column on mobile;
    // on wider screens shows exactly `cols` equal columns (so 1 day fills the
    // row, 3 days give three columns) rather than a fixed 7 with blanks.
    <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", sm: `repeat(${cols}, minmax(160px, 1fr))` }, gap: 1.5, alignItems: "start" }}>
      {activeDays.map((day) => {
        const isToday = day.key === todayKey;
        const open = expandedDay === day.key;
        const shown = open ? day.slots : day.slots.slice(0, SESSION_LIMIT);
        const extra = day.slots.length - shown.length;
        return (
          <Box key={day.key} sx={{
            borderRadius: "12px", border: "1px solid rgba(0,0,0,0.07)",
            bgcolor: isToday ? alpha("#6366f1", 0.03) : "#fff", overflow: "hidden",
          }}>
            <Box sx={{
              px: 1.5, py: 1, borderBottom: "1px solid rgba(0,0,0,0.05)", display: "flex", alignItems: "center", justifyContent: "space-between", gap: 0.5,
              ...(isToday ? { bgcolor: alpha("#6366f1", 0.08) } : {}),
            }}>
              <Box sx={{ display: "flex", alignItems: "center", gap: 0.75, minWidth: 0 }}>
                <Typography sx={{ fontSize: 12.5, fontWeight: 700 }}>{day.label}</Typography>
                {isToday && <Chip label="Today" size="small" sx={{ height: 16, fontSize: 9, fontWeight: 700, bgcolor: "#6366f1", color: "#fff" }} />}
              </Box>
              <Typography sx={{ fontSize: 10.5, color: "text.disabled", whiteSpace: "nowrap" }}>{day.slots.length} {day.slots.length === 1 ? "class" : "classes"}</Typography>
            </Box>
            <Box sx={{ p: day.slots.length ? 0 : 1.25 }}>
              {day.slots.length === 0 ? (
                <Typography sx={{ fontSize: 11, color: "text.disabled", fontStyle: "italic", textAlign: "center", py: 1 }}>No classes</Typography>
              ) : (
                shown.map((slot, idx) => (
                  <Box
                    key={`${slot.class_id}-${slot.start_time}-${idx}`}
                    {...(onViewClass && slot.class_id ? {
                      role: "button", tabIndex: 0,
                      onClick: () => onViewClass(slot.class_id),
                      onKeyDown: (e: React.KeyboardEvent) => { if (e.key === "Enter") onViewClass(slot.class_id); },
                    } : {})}
                    sx={{
                      px: 1.25, py: 0.9, borderBottom: idx < shown.length - 1 ? "1px solid rgba(0,0,0,0.05)" : "none",
                      cursor: onViewClass && slot.class_id ? "pointer" : "default",
                      "&:hover": onViewClass && slot.class_id ? { bgcolor: "rgba(0,0,0,0.02)" } : {},
                      "&:focus-visible": { outline: `2px solid ${alpha("#6366f1", 0.5)}`, outlineOffset: -2 },
                    }}
                  >
                    <Typography sx={{ fontSize: 11, fontWeight: 700, color: "#0ea5e9" }}>{timeRange(slot.start_time, slot.end_time)}</Typography>
                    <Typography sx={{ fontSize: 11.5, fontWeight: 500, lineHeight: 1.25, mt: 0.15 }} noWrap title={slot.name}>{slot.name}</Typography>
                    <Box sx={{ display: "flex", alignItems: "center", gap: 0.5, flexWrap: "wrap", mt: 0.25 }}>
                      {slot.mode && <Typography sx={{ fontSize: 10, color: "text.secondary" }}>{slot.mode}</Typography>}
                      {slot.location && <Typography sx={{ fontSize: 10, color: "text.disabled" }} noWrap>· {slot.location}</Typography>}
                      {slot.student_count > 0 ? (
                        <Typography sx={{ fontSize: 10, color: "text.disabled", display: "flex", alignItems: "center", gap: 0.25 }}>
                          <GroupsOutlinedIcon sx={{ fontSize: 11 }} /> {slot.student_count}
                        </Typography>
                      ) : (
                        <Typography sx={{ fontSize: 9.5, fontStyle: "italic", color: "#b45309" }}>
                          Enrollment pending
                        </Typography>
                      )}
                    </Box>
                  </Box>
                ))
              )}
              {extra > 0 && (
                <Button size="small" onClick={() => setExpandedDay(day.key)} sx={{ fontSize: 10.5, textTransform: "none", width: "100%", justifyContent: "flex-start", px: 1.25, py: 0.5, borderRadius: 0 }}>
                  +{extra} more {extra === 1 ? "class" : "classes"}
                </Button>
              )}
              {open && day.slots.length > SESSION_LIMIT && (
                <Button size="small" onClick={() => setExpandedDay(null)} sx={{ fontSize: 10.5, textTransform: "none", width: "100%", justifyContent: "flex-start", px: 1.25, py: 0.5, borderRadius: 0 }}>
                  Show less
                </Button>
              )}
            </Box>
          </Box>
        );
      })}
    </Box>
  );
};

// ── Calendar view (monthly grid) ────────────────────────────────────────────
const CalendarView: React.FC<{
  month: Dayjs; setMonth: (m: Dayjs) => void;
  grid: ReturnType<typeof buildMonthGrid>;
  eventsByDate: Record<string, any[]>;
  loading: boolean; eventCount: number;
  onEventClick: (e: any) => void;
}> = ({ month, setMonth, grid, eventsByDate, loading, eventCount, onEventClick }) => (
  <Box>
    {/* Month nav */}
    <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mb: 1.5 }}>
      <Typography sx={{ fontSize: 15, fontWeight: 700 }}>{month.format("MMMM YYYY")}</Typography>
      <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
        <IconButton size="small" aria-label="Previous month" onClick={() => setMonth(month.subtract(1, "month").startOf("month"))}><ChevronLeftIcon fontSize="small" /></IconButton>
        <Button size="small" onClick={() => setMonth(dayjs().startOf("month"))} sx={{ textTransform: "none", fontSize: 12, minWidth: 0, px: 1.25 }}>Today</Button>
        <IconButton size="small" aria-label="Next month" onClick={() => setMonth(month.add(1, "month").startOf("month"))}><ChevronRightIcon fontSize="small" /></IconButton>
      </Box>
    </Box>

    {loading ? (
      <Skeleton variant="rounded" height={360} />
    ) : eventCount === 0 ? (
      <Box sx={{ textAlign: "center", py: 6 }}>
        <CalendarMonthOutlinedIcon sx={{ fontSize: 36, color: "rgba(0,0,0,0.1)", mb: 1 }} />
        <Typography sx={{ fontSize: 13, color: "text.secondary" }}>No scheduled classes this month</Typography>
      </Box>
    ) : (
      <Box sx={{ overflowX: "auto" }}>
        <Box sx={{ minWidth: 640 }}>
          {/* Weekday headers */}
          <Box sx={{ display: "grid", gridTemplateColumns: "repeat(7, minmax(0, 1fr))", mb: 0.5 }}>
            {WEEKDAY_HEADERS.map((d) => (
              <Typography key={d} sx={{ fontSize: 10.5, fontWeight: 700, color: "text.secondary", textAlign: "center", textTransform: "uppercase", letterSpacing: 0.4 }}>{d}</Typography>
            ))}
          </Box>
          {/* Weeks */}
          {grid.map((week, wi) => (
            <Box key={wi} sx={{ display: "grid", gridTemplateColumns: "repeat(7, minmax(0, 1fr))", gap: 0.5, mb: 0.5 }}>
              {week.map((cell) => {
                const dayEvents = eventsByDate[cell.iso] ?? [];
                const shown = dayEvents.slice(0, 2);
                const extra = dayEvents.length - shown.length;
                return (
                  <Box key={cell.iso} sx={{
                    minHeight: 84, borderRadius: "8px", p: 0.5,
                    border: cell.isToday ? "1px solid #6366f1" : "1px solid rgba(0,0,0,0.05)",
                    bgcolor: cell.inMonth ? "#fff" : "rgba(0,0,0,0.015)",
                    opacity: cell.inMonth ? 1 : 0.55,
                  }}>
                    <Typography sx={{ fontSize: 10.5, fontWeight: cell.isToday ? 800 : 600, color: cell.isToday ? "#6366f1" : "text.secondary", textAlign: "right", mb: 0.25 }}>
                      {cell.date.date()}
                    </Typography>
                    {shown.map((ev) => {
                      const color = eventColor(ev);
                      // A real session with nobody enrolled yet ("Enrollment
                      // pending"). Dashed border + lighter fill mark it without
                      // losing the subject hue. (is_projected is legacy/always
                      // false now that zero-student sessions are persisted.)
                      const projected = !!ev.enrollment_pending || !!ev.is_projected;
                      return (
                        <Box key={ev.id}
                          role="button" tabIndex={0}
                          onClick={() => onEventClick(ev)}
                          onKeyDown={(e) => { if (e.key === "Enter") onEventClick(ev); }}
                          title={`${timeRange(ev.start_time, ev.end_time)} ${eventTitle(ev)}${projected ? " (enrollment pending)" : ""}`}
                          sx={{
                            display: "block", width: "100%", textAlign: "left", mb: 0.4, px: 0.6, py: 0.4, borderRadius: "5px",
                            bgcolor: alpha(color, projected ? 0.05 : 0.12),
                            borderLeft: `3px ${projected ? "dashed" : "solid"} ${alpha(color, projected ? 0.6 : 1)}`,
                            cursor: "pointer",
                            "&:hover": { bgcolor: alpha(color, projected ? 0.12 : 0.2) },
                            "&:focus-visible": { outline: `2px solid ${alpha(color, 0.6)}`, outlineOffset: 1 },
                          }}>
                          <Typography sx={{ fontSize: 9.5, fontWeight: 700, color, lineHeight: 1.1 }}>{fmtTime(ev.start_time)}</Typography>
                          <Typography sx={{ fontSize: 10, fontWeight: 500, lineHeight: 1.15, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", fontStyle: projected ? "italic" : "normal" }}>{eventTitle(ev)}</Typography>
                        </Box>
                      );
                    })}
                    {extra > 0 && (
                      <Typography onClick={() => onEventClick(dayEvents[shown.length])} sx={{ fontSize: 9.5, color: "text.secondary", cursor: "pointer", px: 0.6, "&:hover": { textDecoration: "underline" } }}>
                        +{extra} more
                      </Typography>
                    )}
                  </Box>
                );
              })}
            </Box>
          ))}
        </Box>
      </Box>
    )}
  </Box>
);

// ── Event details modal ─────────────────────────────────────────────────────
// Two-line label / value stack used by the premium popup body. Each label
// sits above its value — a stronger visual rhythm than the old label-right/
// value-right layout, and it prevents long grade/subject lists from being
// squeezed into a single line.
const Row: React.FC<{ label: string; value?: React.ReactNode }> = ({ label, value }) =>
  value ? (
    <Box sx={{ py: 0.6 }}>
      <Typography sx={{ fontSize: 10.5, fontWeight: 700, color: "text.disabled", textTransform: "uppercase", letterSpacing: 0.4, mb: 0.35 }}>{label}</Typography>
      <Box sx={{ fontSize: 13, fontWeight: 600, color: "text.primary" }}>{value}</Box>
    </Box>
  ) : null;

// Compact summary tile for the top of the popup — Duration / Students /
// Capacity / Attendance. Coloured accent matches the class location.
const SummaryTile: React.FC<{ label: string; value: string; accent: string }> = ({ label, value, accent }) => (
  <Box
    sx={{
      flex: 1,
      minWidth: 0,
      p: 1.25,
      borderRadius: "10px",
      bgcolor: alpha(accent, 0.08),
      border: `1px solid ${alpha(accent, 0.2)}`,
    }}
  >
    <Typography sx={{ fontSize: 9.5, fontWeight: 800, letterSpacing: 0.4, textTransform: "uppercase", color: alpha(accent, 0.85), lineHeight: 1.2 }}>{label}</Typography>
    <Typography sx={{ fontSize: 14, fontWeight: 800, color: "text.primary", mt: 0.35, lineHeight: 1.15 }}>{value}</Typography>
  </Box>
);

const EventModal: React.FC<{
  event: any | null; onClose: () => void;
  // View Class + Edit Schedule are CLASS-level actions — both receive the
  // recurring class id (assign_classes.id, called class_id in the event
  // payload). Mark Attendance is an OCCURRENCE-level action and receives
  // the occurrence_id. Keeping the two arg types distinct here makes the
  // parent's route wiring self-documenting and prevents the previous
  // "occurrence_id → /class-schedules/:id/edit → 404" regression.
  onViewClass?: (classId: number) => void;
  onEditSchedule?: (classId: number) => void;
  onMarkAttendance?: (occurrenceId?: number) => void;
  canEditSchedule?: boolean; canMarkAttendance?: boolean;
}> = ({ event, onClose, onViewClass, onEditSchedule, onMarkAttendance, canEditSchedule, canMarkAttendance }) => {
  if (!event) return null;
  const color = eventColor(event);
  const students = Array.isArray(event.students) ? event.students : [];
  const enrolled = students.length;
  const capacity = Number(event?.capacity ?? event?.number_of_student ?? 0);
  const available = capacity > 0 ? Math.max(capacity - enrolled, 0) : null;
  const durationText = (() => {
    const s = String(event?.start_time || "").split(":").map(Number);
    const e = String(event?.end_time || "").split(":").map(Number);
    if (s.length < 2 || e.length < 2 || [s[0], s[1], e[0], e[1]].some(Number.isNaN)) return "—";
    const mins = (e[0] * 60 + e[1]) - (s[0] * 60 + s[1]);
    if (!Number.isFinite(mins) || mins <= 0) return "—";
    const h = Math.floor(mins / 60), m = mins % 60;
    return h && m ? `${h}h ${m}m` : h ? `${h}h` : `${m}m`;
  })();
  const attendanceStatus = event?.attendance_status || (canMarkAttendance ? "Not marked" : "Not set");
  const badges: Array<{ label: string; bg: string; color: string }> = [];
  if (event?.status) badges.push({ label: String(event.status), bg: alpha(color, 0.14), color });
  if (event?.is_recurring) badges.push({ label: "Recurring", bg: alpha("#0ea5e9", 0.12), color: "#0369a1" });
  if (event?.study_mode && /hybrid/i.test(String(event.study_mode))) {
    badges.push({ label: "Hybrid", bg: "rgba(139,92,246,0.15)", color: "#6d28d9" });
  }
  if (event?.location) badges.push({ label: event.location, bg: alpha(color, 0.12), color });

  return (
    <Dialog
      open={!!event}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      sx={{ "& .MuiPaper-root": { borderRadius: "18px", overflow: "hidden" } }}
    >
      {/* Coloured hero header — location-accent gradient + title/date/time
          stacked with strong hierarchy. Replaces the plain title strip. */}
      <Box
        sx={{
          position: "relative",
          px: { xs: 2, md: 3 },
          pt: 2.5,
          pb: 2,
          background: `linear-gradient(135deg, ${alpha(color, 0.16)} 0%, ${alpha(color, 0.06)} 100%)`,
          borderBottom: `1px solid ${alpha(color, 0.2)}`,
        }}
      >
        <IconButton
          size="small"
          onClick={onClose}
          aria-label="Close"
          sx={{ position: "absolute", top: 10, right: 10, bgcolor: "rgba(255,255,255,0.7)", "&:hover": { bgcolor: "#fff" } }}
        >
          <CloseIcon fontSize="small" />
        </IconButton>
        <Box sx={{ display: "flex", alignItems: "flex-start", gap: 1.25, pr: 4 }}>
          <Box sx={{ width: 6, alignSelf: "stretch", borderRadius: 3, bgcolor: color, flexShrink: 0 }} />
          <Box sx={{ minWidth: 0, flex: 1 }}>
            <Typography sx={{ fontSize: 18, fontWeight: 800, letterSpacing: "-0.01em", lineHeight: 1.2, color: "#0f172a" }}>
              {eventTitle(event)}
            </Typography>
            <Typography sx={{ fontSize: 12.5, fontWeight: 600, color: "text.secondary", mt: 0.4 }}>
              {dayjs(event.date).format("dddd, MMMM D, YYYY")} · {timeRange(event.start_time, event.end_time)}
            </Typography>
            {event?.staff_name && (
              <Typography sx={{ fontSize: 12, color: "text.secondary", mt: 0.35 }}>
                <b>Staff:</b> {event.staff_name}
              </Typography>
            )}
            <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5, mt: 1 }}>
              {badges.map((b, i) => (
                <Chip
                  key={i}
                  label={b.label}
                  size="small"
                  sx={{
                    height: 22,
                    fontSize: 10.5,
                    fontWeight: 700,
                    textTransform: "capitalize",
                    bgcolor: b.bg,
                    color: b.color,
                    border: `1px solid ${alpha(b.color, 0.25)}`,
                  }}
                />
              ))}
            </Box>
          </Box>
        </Box>
      </Box>

      <DialogContent sx={{ p: { xs: 2, md: 2.5 } }}>
        {/* Compact summary tiles — Duration / Students / Capacity / Attendance */}
        <Box sx={{ display: "flex", gap: 1, mb: 1.5, flexWrap: { xs: "wrap", sm: "nowrap" } }}>
          <SummaryTile label="Duration" value={durationText} accent={color} />
          <SummaryTile label="Students" value={String(enrolled)} accent="#2563eb" />
          <SummaryTile label="Capacity" value={capacity > 0 ? String(capacity) : "Not set"} accent="#16a34a" />
          <SummaryTile
            label="Available"
            value={available === null ? "—" : String(available)}
            accent="#f59e0b"
          />
        </Box>

        {(event.enrollment_pending || event.is_projected) && (
          <Box sx={{ mb: 1.5, px: 1.25, py: 1, borderRadius: "10px", bgcolor: alpha("#f59e0b", 0.1), border: `1px dashed ${alpha("#f59e0b", 0.5)}` }}>
            <Typography sx={{ fontSize: 12, fontWeight: 700, color: "#b45309" }}>Enrollment pending</Typography>
            <Typography sx={{ fontSize: 11.5, color: "text.secondary", mt: 0.25 }}>
              This is a real scheduled session with no students enrolled yet. Assign a student to enable attendance and invoicing for this date.
            </Typography>
          </Box>
        )}

        {/* Content sections in a two-column grid on md+ so long lists stay
            readable without doubling the popup height. */}
        <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", md: "1fr 1fr" }, columnGap: 2, rowGap: 0.25 }}>
          <Row
            label="Study Mode"
            value={
              event.study_mode ? (
                <Box sx={{ display: "inline-flex", alignItems: "center", gap: 0.5 }}>
                  <LaptopMacOutlinedIcon sx={{ fontSize: 15, color }} />
                  {event.study_mode}
                </Box>
              ) : undefined
            }
          />
          <Row
            label="Location"
            value={
              event.location ? (
                <Box sx={{ display: "inline-flex", alignItems: "center", gap: 0.5 }}>
                  <LocationOnOutlinedIcon sx={{ fontSize: 15, color }} />
                  {event.location}
                </Box>
              ) : undefined
            }
          />
          <Row label="Session Type" value={event.session_type ? <Box sx={{ textTransform: "capitalize" }}>{event.session_type}</Box> : undefined} />
          <Row label="Recurrence" value={event.is_recurring ? "Recurring session" : "One-off session"} />
          <Row label="Subjects" value={(event.subjects || []).join(", ") || "—"} />
          <Row label="Grades" value={(event.grades || []).join(", ") || "—"} />
          <Row label="Attendance" value={<Box sx={{ textTransform: "capitalize" }}>{attendanceStatus}</Box>} />
          <Row label="Status" value={event.status ? <Box sx={{ textTransform: "capitalize" }}>{event.status}</Box> : undefined} />
        </Box>

        <Divider sx={{ my: 1.5 }} />
        <Typography sx={{ fontSize: 11.5, fontWeight: 800, color: "text.disabled", letterSpacing: 0.4, textTransform: "uppercase", mb: 0.75 }}>
          {enrolled === 0 ? "Students" : `${enrolled} ${enrolled === 1 ? "Student" : "Students"}`}
        </Typography>
        {enrolled === 0 ? (
          <Typography sx={{ fontSize: 12.5, color: "text.secondary", fontStyle: "italic" }}>
            No students are assigned to this session.
          </Typography>
        ) : (
          <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.75 }}>
            {students.map((s: any) => (
              <Box
                key={s.id}
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: 0.75,
                  bgcolor: alpha(color, 0.06),
                  border: `1px solid ${alpha(color, 0.16)}`,
                  borderRadius: "18px",
                  pr: 1.25,
                  pl: 0.35,
                  py: 0.3,
                }}
              >
                <Avatar src={s.image || undefined} sx={{ width: 22, height: 22, fontSize: 10, fontWeight: 700, bgcolor: alpha(color, 0.2), color }}>{(s.name || "?").charAt(0)}</Avatar>
                <Typography sx={{ fontSize: 11.5, fontWeight: 600 }}>{s.name}</Typography>
              </Box>
            ))}
          </Box>
        )}

        {/* Action group — primary on the right, secondaries on the left. */}
        <Box
          sx={{
            display: "flex",
            flexWrap: "wrap",
            gap: 1,
            mt: 2,
            pt: 1.5,
            borderTop: "1px dashed rgba(15,23,42,0.1)",
          }}
        >
          {onViewClass && event.class_id && (
            <Button size="small" variant="outlined" startIcon={<LaunchIcon sx={{ fontSize: "15px !important" }} />} onClick={() => onViewClass(event.class_id)} sx={{ textTransform: "none", fontWeight: 700, borderRadius: "10px" }}>View Class</Button>
          )}
          {/* Edit Schedule is CLASS-level → passes class_id, never occurrence_id. */}
          {canEditSchedule && onEditSchedule && event.class_id && (
            <Button size="small" variant="outlined" startIcon={<EditOutlinedIcon sx={{ fontSize: "15px !important" }} />} onClick={() => onEditSchedule(event.class_id)} sx={{ textTransform: "none", fontWeight: 700, borderRadius: "10px" }}>Edit Schedule</Button>
          )}
          <Box sx={{ flexGrow: 1 }} />
          {canMarkAttendance && onMarkAttendance && (
            <Button
              size="small"
              variant="contained"
              startIcon={<HowToRegOutlinedIcon sx={{ fontSize: "15px !important" }} />}
              onClick={() => onMarkAttendance(event.occurrence_id)}
              sx={{
                textTransform: "none",
                fontWeight: 700,
                borderRadius: "10px",
                background: `linear-gradient(135deg, ${color} 0%, ${alpha(color, 0.85)} 100%)`,
                boxShadow: `0 4px 12px ${alpha(color, 0.35)}`,
                "&:hover": { boxShadow: `0 6px 16px ${alpha(color, 0.45)}` },
              }}
            >
              Mark Attendance
            </Button>
          )}
        </Box>
      </DialogContent>
    </Dialog>
  );
};

export default WeeklySchedulePlanner;
